import React from 'react';
import DOMPurify from 'dompurify';

/**
 * Detects markup produced by the rich-text editor. Legacy rows hold plain text
 * (often with meaningful newlines), so both shapes must render correctly from
 * the same column forever — there is no migration that converts old content.
 */
const looksLikeHtml = (value: string): boolean => /<([a-z][\w-]*)(\s|>|\/)/i.test(value);

// Moved to utils/richText.ts (dependency-free, so pure display modules can
// import them without pulling react + dompurify); re-exported here so the
// existing `from '../components/RichContent'` importers keep working.
export { richTextIsEmpty, normalizeRichText } from '../utils/richText';

interface RichContentProps {
  /** Stored field value — sanitized HTML from the editor, or legacy plain text. */
  html: string;
  /** Classes for the wrapping element (typography, color). */
  className?: string;
}

/**
 * Renders admin-authored rich text safely: HTML is DOMPurify-sanitized and
 * gets the global `.prose` styles (lists, headings — Tailwind preflight strips
 * them otherwise); plain text keeps its whitespace exactly as typed.
 *
 * Use this everywhere a Quill-edited field is displayed, public surfaces
 * included — never a bare dangerouslySetInnerHTML.
 */
const RichContent: React.FC<RichContentProps> = ({ html, className = '' }) => {
  if (!looksLikeHtml(html)) {
    return <p className={`whitespace-pre-wrap ${className}`}>{html}</p>;
  }
  return (
    <div
      className={`prose max-w-none ${className}`}
      dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(html) }}
    />
  );
};

export default RichContent;
